/*
 * The full report: what the cleanup rules decide — DELETE or KEEP, and why —
 * for every workspace and every user, kept ones too.
 *
 * The database side (workspaces, users, which users are test accounts, the
 * cleanup list) comes from the backend; billing is read here, directly. The
 * rules are the backend scan's (unified-backend utils/dataCleanupScan.ts),
 * applied to the same data — a change to a rule there belongs here too.
 *
 * A workspace is dead when:
 *   - it is older than 6 months and has had no credit use in the last 6, or
 *   - its subscription has ended: every credit grant is past its expiry/grace
 *     date (or it never had one and is older than 6 months), or
 *   - it is older than 6 months and billing has no record of it.
 * Then, first rule that matches: billing failed → KEEP; paid → KEEP; created by
 * a test account → DELETE; not dead → KEEP; owner gone → DELETE; subscription
 * ended → DELETE; owner's only workspace → KEEP; all the owner's workspaces dead
 * → KEEP the most recently used, DELETE the rest; else DELETE. A DELETE that
 * shares credits in an active credit pool (or whose pool billing can't answer
 * for) becomes KEEP.
 */

export interface ReportData {
  workspaces: {
    _id: string;
    name: string;
    addedBy: string;
    createdAt: string;
  }[];
  users: {
    _id: string;
    name: string;
    email: string;
    createdAt: string | null;
    /** Why they look like a test account, or null. */
    testAccount: string | null;
    /** A manage-dashboard admin who looks like one: never deleted. */
    looksLikeTest: string | null;
  }[];
  listStatus: { type: "workspace" | "user"; refId: string; status: string }[];
}

export interface ReportRow {
  type: "workspace" | "user";
  action: "DELETE" | "KEEP";
  reason: string;
  workspaceId?: string;
  workspaceName?: string;
  /** For a user row: the user. */
  ownerId: string;
  ownerName: string;
  ownerEmail: string;
  /** Active workspaces the owner (or, for a user, they) created. */
  ownerWorkspaces: number;
  /** free | paid | custom | none (no subscription); empty if billing failed. */
  planType: string;
  planName: string;
  createdAt: Date | null;
  /** Newest credit use; for a user, across the workspaces they created. */
  lastActive: Date | null;
  /** Where it is on the cleanup list now (watching, excluded…), or empty. */
  listStatus: string;
}

/** Billing can't be used for a report that would be right; nothing is shown. */
export class BillingUnusableError extends Error {}

const PARALLEL_CALLS = 15;
const TIMEOUT_MS = 5000;
const TRIES = 3;
const GRANTS = 20;
const MAX_FAILED_SHARE = 0.05;
const MAX_UNKNOWN_SHARE = 0.25;
const MAX_BILLING_AGE_DAYS = 7;

// "3 Mar 2026", as the scan writes reasons.
const day = (d: Date) =>
  d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

const NOT_FOUND = Symbol("not found");
type Fetch = typeof fetch;

// The parts of billing's answers the rules read.
interface Grant {
  createdAt?: string;
  expiresAt?: string;
  gracePeriodEndsAt?: string;
}
interface Subscription {
  plan?: { name?: string; amount?: number | string; type?: string };
}
interface UsageHistory {
  data?: Grant[];
}
interface Subscriptions {
  data?: { workspaceId: string; subscription: Subscription | null }[];
}
interface CreditPool {
  data?: {
    members?: { status?: string }[];
    sponsor?: { status?: string } | null;
  };
}

/** Billing's JSON, or NOT_FOUND when it has never heard of the workspace (404). */
const billingJson = async <T>(
  fetchFn: Fetch,
  url: string,
): Promise<T | typeof NOT_FOUND> => {
  for (let attempt = 1; ; attempt++) {
    try {
      const res = await fetchFn(url, {
        signal: AbortSignal.timeout(TIMEOUT_MS),
      });
      if (res.status === 404) return NOT_FOUND;
      if (!res.ok) throw new Error(`billing ${res.status}`);
      return (await res.json()) as T;
    } catch (err) {
      if (attempt >= TRIES) throw err;
    }
  }
};

const pool = async <T, R>(
  items: T[],
  fn: (item: T) => Promise<R>,
  onDone?: () => void,
) => {
  const out = new Array<R>(items.length);
  let next = 0;
  await Promise.all(
    Array.from({ length: PARALLEL_CALLS }, async () => {
      while (next < items.length) {
        const i = next++;
        out[i] = await fn(items[i]);
        onDone?.();
      }
    }),
  );
  return out;
};

/** free: a $0 plan; paid: a standard priced one; custom: priced but not standard recurring. */
export const planType = (s?: Subscription | null): string => {
  if (!s?.plan) return "none";
  if (
    /custom|enterprise/i.test(s.plan.name ?? "") ||
    s.plan.type !== "recurring"
  ) {
    return "custom";
  }
  return Number(s.plan.amount) > 0 ? "paid" : "free";
};

interface Options {
  /** CENTRALISED_BILLING_API: billing's base URL, ending in /v1. */
  billing: string;
  /** Called as billing answers: how many workspaces are done, of how many. */
  onProgress?: (done: number, total: number) => void;
  fetchFn?: Fetch;
  now?: Date;
}

export const buildReport = async (
  data: ReportData,
  { billing, onProgress, fetchFn = fetch, now = new Date() }: Options,
): Promise<ReportRow[]> => {
  const sixMonthsAgo = new Date(now);
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

  // Without billing every workspace would look unused.
  const health = await fetchFn(`${new URL(billing).origin}/health`, {
    signal: AbortSignal.timeout(TIMEOUT_MS),
  }).catch(() => null);
  if (!health?.ok) {
    throw new BillingUnusableError(
      `The billing service at ${billing} is not answering, so the report cannot tell which workspaces are used.`,
    );
  }

  const userById = new Map(data.users.map((u) => [String(u._id), u]));
  const listStatus = new Map(
    data.listStatus.map((c) => [`${c.type}:${c.refId}`, c.status]),
  );
  const ownedBy = new Map<string, string[]>();
  for (const w of data.workspaces) {
    const owner = String(w.addedBy);
    if (!ownedBy.has(owner)) ownedBy.set(owner, []);
    ownedBy.get(owner)!.push(String(w._id));
  }

  // Billing: newest deduct and the credit grants, for every workspace.
  let done = 0;
  const rows = await pool(
    data.workspaces,
    async (w) => {
      const id = String(w._id);
      let deduct: UsageHistory | typeof NOT_FOUND | undefined,
        add: UsageHistory | typeof NOT_FOUND | undefined,
        failed = false;
      try {
        deduct = await billingJson<UsageHistory>(
          fetchFn,
          `${billing}/credits/get-usage-history/${id}?types=deduct&limit=1`,
        );
        if (deduct !== NOT_FOUND) {
          // Several grants: a later short top-up must not hide a longer one.
          add = await billingJson<UsageHistory>(
            fetchFn,
            `${billing}/credits/get-usage-history/${id}?types=add&limit=${GRANTS}`,
          );
        }
      } catch {
        failed = true;
      }
      const grants: Grant[] = add === NOT_FOUND ? [] : (add?.data ?? []);
      const lastDeduct = deduct === NOT_FOUND ? undefined : deduct?.data?.[0];
      const creditsEnd = Math.max(
        0,
        ...grants.map((g) =>
          Math.max(
            g.gracePeriodEndsAt ? new Date(g.gracePeriodEndsAt).getTime() : 0,
            g.expiresAt ? new Date(g.expiresAt).getTime() : 0,
          ),
        ),
      );
      const owner = userById.get(String(w.addedBy));
      return {
        id,
        name: w.name,
        createdAt: new Date(w.createdAt),
        ownerId: String(w.addedBy),
        ownerName: owner?.name ?? "",
        ownerEmail: owner?.email ?? "",
        ownerExists: !!owner,
        lastDeductAt: lastDeduct?.createdAt
          ? new Date(lastDeduct.createdAt)
          : null,
        lastAddAt: grants[0]?.createdAt ? new Date(grants[0].createdAt) : null,
        creditsExpireAt: creditsEnd ? new Date(creditsEnd) : null,
        failed,
        unknown: deduct === NOT_FOUND,
        planType: "",
        planName: "",
        paid: false,
        dead: false,
        expired: false,
        reason: "",
      };
    },
    () => onProgress?.(++done, data.workspaces.length),
  );
  type Row = (typeof rows)[number];

  // Plans, 10 workspaces per call.
  const byId = new Map(rows.map((r) => [r.id, r]));
  const batches: Row[][] = [];
  for (let i = 0; i < rows.length; i += 10) batches.push(rows.slice(i, i + 10));
  await pool(batches, async (batch) => {
    try {
      const res = await billingJson<Subscriptions>(
        fetchFn,
        `${billing}/subscriptions/current-new/${batch.map((r) => r.id).join(",")}`,
      );
      const answered = res === NOT_FOUND ? [] : (res?.data ?? []);
      for (const { workspaceId, subscription: s } of answered) {
        const r = byId.get(String(workspaceId));
        if (!r) continue;
        r.paid = !!s && Number(s.plan?.amount) > 0;
        r.planType = planType(s);
        r.planName = s?.plan?.name ?? "";
      }
    } catch {
      for (const r of batch) r.failed = true;
    }
  });

  // Answers that are not a picture of the whole system: show nothing.
  const failedCount = rows.filter((r) => r.failed).length;
  if (rows.length && failedCount / rows.length > MAX_FAILED_SHARE) {
    throw new BillingUnusableError(
      `Billing did not answer for ${failedCount} of ${rows.length} workspaces. Try again in a few minutes.`,
    );
  }
  const unknownCount = rows.filter((r) => r.unknown).length;
  if (rows.length && unknownCount / rows.length > MAX_UNKNOWN_SHARE) {
    throw new BillingUnusableError(
      `The billing service at ${billing} has no record of ${unknownCount} of ${rows.length} workspaces, so it holds a different environment's data.`,
    );
  }
  const newestActivity = Math.max(
    0,
    ...rows.flatMap((r) =>
      [r.lastDeductAt, r.lastAddAt].map((d) => d?.getTime() ?? 0),
    ),
  );
  if (
    rows.length &&
    now.getTime() - newestActivity > MAX_BILLING_AGE_DAYS * 86_400_000
  ) {
    throw new BillingUnusableError(
      `The newest credit activity the billing service at ${billing} has is ${newestActivity ? `from ${day(new Date(newestActivity))}` : "none at all"}, so its data is out of date.`,
    );
  }

  for (const r of rows) {
    if (r.unknown) {
      r.dead = r.createdAt < sixMonthsAgo;
      r.reason = r.dead ? "Billing has no record of this workspace" : "";
      continue;
    }
    const reasons: string[] = [];
    if (
      r.createdAt < sixMonthsAgo &&
      !(r.lastDeductAt && r.lastDeductAt > sixMonthsAgo)
    ) {
      reasons.push(
        r.lastDeductAt
          ? `No credits used since ${day(r.lastDeductAt)}`
          : "Never used any credits",
      );
    }
    r.expired =
      !r.paid &&
      (r.creditsExpireAt
        ? r.creditsExpireAt <= now
        : r.createdAt < sixMonthsAgo);
    if (r.expired) {
      reasons.push(
        r.creditsExpireAt
          ? `Subscription ended on ${day(r.creditsExpireAt)}`
          : "Never had a subscription",
      );
    }
    r.dead = reasons.length > 0;
    r.reason = reasons.join("; ");
  }

  // Most recently used: newest deduct, else when it was made (from the id, to
  // the second); on a tie the higher id — the same pick as the scan's.
  const lastUsed = (r: Row) =>
    r.lastDeductAt?.getTime() ?? parseInt(r.id.slice(0, 8), 16) * 1000;
  const newer = (a: Row, b: Row) =>
    lastUsed(b) > lastUsed(a) || (lastUsed(b) === lastUsed(a) && b.id > a.id);

  type Decision = { action: "DELETE" | "KEEP"; reason: string };
  const keep = (reason: string): Decision => ({ action: "KEEP", reason });
  const remove = (reason: string): Decision => ({ action: "DELETE", reason });
  /** `base; but why` — or just `why`, capitalised, when there is no base. */
  const but = (base: string, why: string) =>
    base ? `${base}; but ${why}` : why[0].toUpperCase() + why.slice(1);

  const decide = (r: Row): Decision => {
    if (r.failed)
      return keep("Billing did not answer for this workspace; try again");
    const testReason = userById.get(r.ownerId)?.testAccount;
    const test = testReason
      ? `Created by a test account (${testReason})` +
        (r.reason ? `; ${r.reason}` : "")
      : "";
    if (r.paid)
      return keep(but(test || r.reason, "it has a paid subscription"));
    if (test) return remove(test);
    if (!r.dead) {
      if (r.unknown)
        return keep(
          `Billing has no record of it yet; created on ${day(r.createdAt)}, under 6 months ago`,
        );
      if (r.lastDeductAt && r.lastDeductAt > sixMonthsAgo)
        return keep(`In use: credits used on ${day(r.lastDeductAt)}`);
      return keep(`New: created on ${day(r.createdAt)}, under 6 months ago`);
    }
    if (!r.ownerExists)
      return remove(
        `${r.reason}; the account that created it no longer exists`,
      );
    if (r.expired) return remove(r.reason);
    // Expired siblings are deleted anyway, so they don't count as the owner's.
    const siblings = ownedBy
      .get(r.ownerId)!
      .map((id) => byId.get(id)!)
      .filter((s) => !s.expired);
    if (siblings.length === 1)
      return keep(but(r.reason, "it is the owner's only workspace"));
    const anyAlive = siblings.some((s) => !s.dead || s.paid || s.failed);
    const keeper = siblings.reduce((a, b) => (newer(a, b) ? b : a));
    if (!anyAlive && keeper === r)
      return keep(
        but(r.reason, "it is the owner's most recently used workspace"),
      );
    return remove(r.reason);
  };
  const decisions = new Map(rows.map((r) => [r.id, decide(r)]));

  // Shared credit pools, for what the rules would delete.
  await pool(
    rows.filter((r) => decisions.get(r.id)!.action === "DELETE"),
    async (r) => {
      const { reason } = decisions.get(r.id)!;
      try {
        const res = await billingJson<CreditPool>(
          fetchFn,
          `${billing}/credit-pool/${r.id}`,
        );
        if (res === NOT_FOUND) return;
        const shared = res?.data;
        if (
          (shared?.members ?? []).some((m) => m?.status === "active") ||
          shared?.sponsor?.status === "active"
        ) {
          decisions.set(
            r.id,
            keep(but(reason, "it shares credits in an active credit pool")),
          );
        }
      } catch {
        decisions.set(
          r.id,
          keep(
            but(
              reason,
              "billing did not answer about its credit pool; try again",
            ),
          ),
        );
      }
    },
  );

  const lastActiveOf = new Map<string, Date>();
  for (const r of rows) {
    const prev = lastActiveOf.get(r.ownerId);
    if (r.lastDeductAt && !(prev && prev >= r.lastDeductAt))
      lastActiveOf.set(r.ownerId, r.lastDeductAt);
  }

  const report: ReportRow[] = rows.map((r) => ({
    type: "workspace",
    ...decisions.get(r.id)!,
    workspaceId: r.id,
    workspaceName: r.name,
    ownerId: r.ownerId,
    ownerName: r.ownerName,
    ownerEmail: r.ownerEmail,
    ownerWorkspaces: ownedBy.get(r.ownerId)?.length ?? 0,
    planType: r.planType,
    planName: r.planName,
    createdAt: r.createdAt,
    lastActive: r.lastDeductAt,
    listStatus: listStatus.get(`workspace:${r.id}`) ?? "",
  }));
  for (const u of data.users) {
    const id = String(u._id);
    report.push({
      type: "user",
      action: u.testAccount ? "DELETE" : "KEEP",
      reason: u.testAccount
        ? `Test account (${u.testAccount})`
        : u.looksLikeTest
          ? `Looks like a test account (${u.looksLikeTest}); but is a manage-dashboard admin`
          : "Not a test account",
      ownerId: id,
      ownerName: u.name,
      ownerEmail: u.email,
      ownerWorkspaces: ownedBy.get(id)?.length ?? 0,
      planType: "",
      planName: "",
      createdAt: u.createdAt ? new Date(u.createdAt) : null,
      lastActive: lastActiveOf.get(id) ?? null,
      listStatus: listStatus.get(`user:${id}`) ?? "",
    });
  }
  return report;
};
